import { useQuery } from '@tanstack/react-query'
import { pfetch } from '@/lib/phishtrainer/play-api'
import { playKeys } from '@/lib/phishtrainer/query-keys'
import type { KnowledgeProfile } from '@/lib/phishtrainer/types'

// ============ Types ============

interface KnowledgeEnvelope {
  data?: { profile?: KnowledgeProfile | null } | KnowledgeProfile | null
}

// ============ Normalizer ============

/**
 * The endpoint returns the profile either bare or wrapped in `{ profile }`
 * depending on which realm answered, and every consumer was unwrapping it by
 * hand. Do it once, here.
 *
 * Returns null rather than a zeroed profile on absence — deliberately. A
 * synthesised empty profile renders as a real "0 / Beginner" score, which reads
 * to the learner as a measured result rather than as missing data; the page must
 * be able to tell the two apart.
 */
function normalizeProfile(raw: KnowledgeEnvelope | null | undefined): KnowledgeProfile | null {
  const d = raw?.data
  if (!d) return null
  const profile = (d as { profile?: KnowledgeProfile | null }).profile ?? (d as KnowledgeProfile)
  return profile && typeof profile === 'object' && 'signals' in profile ? profile : null
}

// ============ Query Hooks ============

/**
 * The learner's knowledge profile (per-pillar mastery, cue breakdown, overall).
 *
 * `userId` is the admin deep-link override (?userId=) — the proxy prefers the
 * recipient session when one is active, so inside the play realm this still
 * resolves the logged-in learner. NOT part of the query key: isolation is by
 * eviction (PlayCacheGuard), and keying by learner would leave the previous
 * learner's profile resident under their own key.
 */
export function usePlayKnowledge(userId: string | undefined) {
  return useQuery({
    queryKey: playKeys.knowledge(),
    queryFn: async (): Promise<KnowledgeEnvelope> => {
      const res = await pfetch(`/api/phishtrainer/users/${userId}/knowledge`)
      if (!res.ok) throw new Error('Could not load your progress')
      return res.json()
    },
    select: normalizeProfile,
    enabled: !!userId,
  })
}
